<template>
  <div class="view-project" :class="{ 'view-project--display': isDisplayView }">
    <b-navbar class="project-navbar" toggleable="lg" type="light" variant="light">
      <div v-if="currentDisplay" class="d-flex align-items-center mr-2 display-title-group">
        <span class="mr-2 text-truncate display-title">{{ currentDisplay.title }}</span>
        <b-button size="sm" variant="outline-primary" @click="toggleDisplayFullscreen">
          <i class="fa fa-laptop" />
        </b-button>
      </div>
      <b-navbar-brand>
        {{getConnectionStatus()}}
        <i class="fa fa-circle" :style="getConnectionStatusColor()" />
      </b-navbar-brand>

      <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav>
          <b-nav-item @click="stopConnection()">
            {{ $t('project.projectViewer.stopConnection') }}
            <i class="fa fa-stop-circle" />
          </b-nav-item>
          <b-nav-item @click="startConnection()">
            {{ $t('project.projectViewer.reconnect') }}
            <i class="fa fa-play-circle" />
          </b-nav-item>
          <b-nav-item v-if="decideCommandAdminRights()" @click="commandRights = !commandRights">
            <b-badge :variant="commandRights ? 'success' : 'danger'">
              {{commandRights ? "ON" : "OFF"}}
            </b-badge>
          </b-nav-item>
        </b-navbar-nav>

        <!-- Right aligned nav items -->
        <b-navbar-nav class="ml-auto">
          <b-nav-item
            right
            @click="changeComponent('MainPage', null)"
          >{{ $t('project.projectViewer.mainPage') }}</b-nav-item>

          <b-nav-item-dropdown :text="$t('project.projectViewer.modules')" right>
            <b-dropdown-item
              v-for="(value, key) in data.modulesLazy"
              :key="'t-m-' + key"
              @click="changeModuleComponent(value.id, value.shortname)"
            >{{value.shortname}}</b-dropdown-item>
          </b-nav-item-dropdown>

          <b-nav-item-dropdown :text="$t('project.projectViewer.displays')" right>
            <b-dropdown-item
              v-for="(value, key) in sortedDisplaysLazy"
              :key="'t-d-' + key"
              @click="changeDisplayComponent(value.id, value.name)"
            >{{value.name}}</b-dropdown-item>
          </b-nav-item-dropdown>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <b-card :class="{ 'display-fill-card': isDisplayView }">
      <div v-if="loading" class="mb-3 mt-3">
        <b-spinner label="Loading..."></b-spinner>
      </div>

      <b-form v-if="currentPage == 'MainPage'">
        <b-input-group class="mb-3">
          <b-input-group-prepend>
            <b-input-group-text>{{ $t('project.projListName') }}</b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            type="text"
            class="form-control"
            v-model="data.projectname"
            name="name"
            disabled
          />
        </b-input-group>

        <b-input-group class="mb-3">
          <b-input-group-prepend>
            <b-input-group-text>{{ $t('project.projLocation') }}</b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            type="text"
            class="form-control"
            v-model="data.location"
            name="location"
            disabled
          />
        </b-input-group>

        <b-input-group class="mb-3">
          <b-input-group-prepend>
            <b-input-group-text>{{ $t('project.projListDesc') }}</b-input-group-text>
          </b-input-group-prepend>
          <b-form-textarea
            class="form-control"
            v-model="data.description"
            name="description"
            rows="3"
            max-rows="6"
            no-resize
            disabled
          />
        </b-input-group>

        <b-button-group class="mb-3">
          <router-link
            v-if="decideAetAdminRights()"
            class="mr-1"
            :to="`/project/alarms/current/${projectId}`"
          >
            <b-button
              size="md"
              v-b-tooltip.hover
              :title="$t('project.alarmsExport.currentAlarms')"
              variant="danger"
            >
              <i class="icon-bell" />
            </b-button>
          </router-link>
          <router-link v-if="decideAetAdminRights()" class="mr-1" :to="`/project/events/${projectId}`">
            <b-button
              size="md"
              v-b-tooltip.hover
              :title="$t('project.eventsExport.eventsProject')"
              variant="outline-info"
            >
              <i class="icon-note" />
            </b-button>
          </router-link>
          <router-link v-if="decideAetAdminRights()" class="mr-1" :to="`/project/trends/${projectId}`">
            <b-button
              size="md"
              v-b-tooltip.hover
              :title="$t('project.trendsExport.trendsProject')"
              variant="outline-primary"
            >
              <i class="icon-notebook" />
            </b-button>
          </router-link>
          <router-link v-if="decideAetAdminRights()" class="mr-1" :to="`/project/alarms/${projectId}`">
            <b-button
              size="md"
              v-b-tooltip.hover
              :title="$t('project.alarmsExport.alarmsProject')"
              variant="outline-danger"
            >
              <i class="icon-bell" />
            </b-button>
          </router-link>
          <router-link v-if="decideTimeScheduleAdminRights()" class="mr-1" :to="`/project/schedules/${projectId}`">
            <b-button
              size="md"
              v-b-tooltip.hover
              :title="$t('project.schedulesExport.schedulesProject')"
              variant="outline-dark"
            >
              <i class="icon-clock" />
            </b-button>
          </router-link>
        </b-button-group>

        <b-button
          v-if="decideCommandAdminRights()"
          class="mb-3"
          :pressed.sync="commandRights"
          :variant="commandRights ? 'success' : 'danger'"
        >{{commandRights ? $t('project.projectViewer.commandRightsOn') : $t('project.projectViewer.commandRightsOff')}}</b-button>

        <div v-if="decideCommandAdminRights() && watchdogStationOptions.length" class="mb-3">
          <div class="small mb-1">{{ $t('project.projectViewer.watchdogLastMessage') }}</div>
          <div class="watchdog-row">
            <b-form-select
              v-model="watchdogStationId"
              :options="watchdogStationOptions"
              size="sm"
              class="watchdog-select"
            />
            <b-button
              size="sm"
              variant="info"
              v-b-tooltip.hover
              :title="$t('project.projectViewer.watchdog')"
              :disabled="watchdogStationId == null"
              @click="showWatchdog"
            >
              <i class="icon-clock" />
            </b-button>
            <span class="watchdog-value small">{{ getFormatDateString(watchdog) }}</span>
          </div>
        </div>

        <b-form-group
          id="displays-1"
          :label="$t('project.projectViewer.displays')"
          label-for="displays-1"
        >
          <b-list-group>
            <b-list-group-item
              button
              v-for="(value, key) in sortedDisplaysLazy"
              :key="'b-d-' + key"
              @click="changeDisplayComponent(value.id, value.name)"
            >{{value.name}} - {{value.title}}</b-list-group-item>
          </b-list-group>
        </b-form-group>
        <b-form-group
          id="modules-1"
          :label="$t('project.projectViewer.modules')"
          label-for="modules-1"
        >
          <b-list-group>
            <b-list-group-item
              button
              v-for="(value, key) in data.modulesLazy"
              :key="'b-m-' + key"
              @click="changeModuleComponent(value.id, value.shortname)"
            >{{value.shortname}} - {{value.longname}}</b-list-group-item>
          </b-list-group>
        </b-form-group>
      </b-form>
      <span
        v-for="(value, key) in data.displays"
        :key="'d-' + key"
        :class="{ 'display-host': value.name == currentPage }"
      >
        <keep-alive>
          <displayView
            v-if="value.name == currentPage"
            ref="displayView"
            :display="value"
            :adminRights="decideCommandAdminRights() && commandRights"
            :projectId="projectId"
            @sendValue="sendValueFromDisplay"
            @showInfoError="showInfoError"
          />
        </keep-alive>
      </span>
      <span v-for="(value, key) in data.modules" :key="'m-' + key">
        <keep-alive>
          <moduleView
            v-if="value.shortname == currentPage"
            :moduleData="value"
            :adminRights="decideCommandAdminRights()"
            @sendValue="sendValueFromModule"
          />
        </keep-alive>
      </span>

      <div v-if="errored" :class="{ 'display-status-alert': isDisplayView }">
        <b-alert show variant="danger" dismissible fade @dismissed="errored = null">{{errored}}</b-alert>
      </div>
      <div v-if="informed" :class="{ 'display-status-alert': isDisplayView }">
        <b-alert show variant="success" dismissible @dismissed="informed = null" fade>{{informed}}</b-alert>
      </div>
    </b-card>
  </div>
</template>

<script>
import { projectService } from "@/_services";
import { router } from "@/router";
import { errorHandler, getAppQuery } from "@/_helpers";
import {
  HubConnectionBuilder,
  LogLevel,
  HttpTransportType,
} from "@aspnet/signalr";
import config from "@/_helpers/_config";
import ModuleViewer from "@/components/projects/ModuleViewer";
import DisplayViewer from "@/components/projects/DisplayViewer";

export default {
  name: "ViewProject",
  components: {
    moduleView: ModuleViewer,
    displayView: DisplayViewer,
  },
  data() {
    return {
      projectId: null,
      user: {},
      data: {},
      currentPage: "MainPage",
      messageType: null,
      mapModule: new Map(),
      mapDisplayData: new Map(),
      connection: null,
      connected: false,
      timer: null,
      commandRights: false,
      watchdogStationId: null,
      watchdog: null,
      loading: false,
      errored: null,
      informed: null,
      moduleSetCallback: null,
    };
  },
  computed: {
    currentDisplay() {
      const displays = this.data.displays;
      if (!displays || !this.currentPage) return null;
      return displays.find((d) => d.name === this.currentPage) || null;
    },
    isDisplayView() {
      return !!this.currentDisplay;
    },
    sortedDisplaysLazy() {
      return (this.data && this.data.displaysLazy) || [];
    },
    watchdogStationOptions() {
      return (this.data.modulesLazy || []).map((item) => ({
        value: item.id,
        text: item.longname ? `${item.shortname} - ${item.longname}` : item.shortname,
      }));
    },
  },
  watch: {
    "$route.query": {
      deep: true,
      handler() {
        if (this.data && (this.data.displaysLazy || this.data.modulesLazy)) {
          this.applyQueryTarget();
        }
      }
    },
    watchdogStationId() {
      this.watchdog = null;
    },
  },
  created() {
    this.projectId = parseInt(this.$route.params.projectId, 10);
    if (isNaN(this.projectId) || this.projectId < 0) {
      router.push("/error404");
      return;
    }

    this.user = JSON.parse(localStorage.getItem("viewer-user"));
    this.loading = true;
    projectService
      .getProjectInfo(this.projectId)
      .then((response) => {
        this.data = response.data;
        if (this.data.modulesLazy && this.data.modulesLazy.length)
          this.watchdogStationId = this.data.modulesLazy[0].id;
        this.initConnection();
        this.applyQueryTarget();
      })
      .catch((error) => {
        this.errored = errorHandler(error);
      })
      .finally(() => this.loading = false);
  },
  beforeDestroy() {
    if (this.connection == null) return;
    this.connection.stop();
  },
  methods: {
    clearInfErr() {
      this.errored = null;
      this.informed = null;
    },
    initConnection() {
      var urlBuilder = `${config.WS_LOCATION}?project=${this.projectId}`;
      this.connection = new HubConnectionBuilder()
        .withUrl(urlBuilder, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
          accessTokenFactory: () => this.user.authdata,
        })
        .configureLogging(LogLevel.Error)
        .build();

      this.connection.on("ModuleDataMessage", (message) => {
        this.onModuleDataReceive(message);
      });

      this.connection.on("DisplayDataMessage", (message) => {
        this.onDisplayDataReceive(message);
      });

      this.connection.on("VariableValueSet", (message) => {
        if (message === null)
          this.informed = this.$i18n.t("project.projectViewer.varSuccSet");
        else this.errored = message;

        if (this.moduleSetCallback) {
          this.moduleSetCallback(message === null ? 1 : -1);
          this.moduleSetCallback = null;
        }
      });

      this.connection.onclose(() => {
        this.connected = false;
        clearInterval(this.timer);
      });

      this.startConnection();
    },
    startConnection() {
      if (this.connection == null) {
        this.errored = this.$i18n.t("project.projectViewer.errInitWs");
        return;
      }
      if (this.connected) return;
      this.clearInfErr();
      this.connection
        .start()
        .then(() => {
          this.connected = true;
          this.timer = setInterval(this.timerFunction, 5000);
          this.timerFunction();
        })
        .catch(() => {
          this.errored = this.$i18n.t("project.projectViewer.errStartWs");
        });
    },
    stopConnection() {
      if (!this.connected) return;
      this.clearInfErr();
      this.connection.stop();
    },
    timerFunction() {
      if (!this.connected || !this.connection) return;
      switch (this.messageType) {
        case "ModuleDataRequest":
          this.connection
            .invoke("ModuleDataRequest", this.currentPage)
            .catch((err) => {
              this.errored = this.$i18n.t(
                "project.projectViewer.errInvokeData"
              );
              console.log(err);
            });
          break;
        case "DisplayDataRequest":
          var index = this.mapDisplayData.get(this.currentPage);
          this.connection
            .invoke(
              "DisplayDataRequest",
              this.data.displays[index].blobObject,
              this.currentPage
            )
            .catch((err) => {
              this.errored = this.$i18n.t(
                "project.projectViewer.errInvokeData"
              );
              console.log(err);
            });
          break;
      }
    },
    onModuleDataReceive(message) {
      if (message.success) {
        let i = this.mapModule.get(message.moduleName);
        if (i === undefined) return;
        for (var j = 0; j < this.data.modules[i].objects.length; j++) {
          for (
            var k = 0;
            k < this.data.modules[i].objects[j].properties.length;
            k++
          ) {
            for (
              var m = 0;
              m <
              this.data.modules[i].objects[j].properties[k].variables.length;
              m++
            ) {
              let variable = this.data.modules[i].objects[j].properties[k]
                .variables[m].variable;
              let varIndex = message.variables.findIndex(
                (x) => this.alterVariableCases(x.item1) === this.alterVariableCases(variable)
              );
              if (varIndex === -1) {
                this.data.modules[i].objects[j].properties[k].variables[
                  m
                ].value = "---";
              } else {
                this.data.modules[i].objects[j].properties[k].variables[
                  m
                ].value = message.variables[varIndex].item2;
              }
              this.data.modules[i].objects[j].properties[k].variables[
                m
              ].tmpVal = null;
            }
          }
        }
      } else {
        console.log(message.failMessage);
        this.errored = this.$i18n.t("project.projectViewer.errGetData");
      }
    },
    onDisplayDataReceive(message) {
      if (message.success) {
        let index = this.mapDisplayData.get(this.currentPage);
        if (index === undefined) return;
        this.data.displays[index].blobObject = message.blobObject;
      } else {
        console.log(message.failMessage);
        this.errored = this.$i18n.t("project.projectViewer.errGetData");
      }
    },
    applyQueryTarget() {
      const query = getAppQuery(this.$route);
      if (query.display != null && query.display !== "") {
        const displayId = parseInt(query.display, 10);
        const display = (this.data.displaysLazy || []).find(
          (item) => Number(item.id) === displayId
        );
        if (display) {
          if (this.currentPage !== display.name) {
            this.changeDisplayComponent(display.id, display.name);
          }
          return;
        }
      }
      if (query.device != null && query.device !== "") {
        const deviceId = parseInt(query.device, 10);
        const device = (this.data.modulesLazy || []).find(
          (item) => Number(item.id) === deviceId
        );
        if (device) {
          if (this.currentPage !== device.shortname) {
            this.changeModuleComponent(device.id, device.shortname);
          }
        }
      }
    },
    changeDisplayComponent(id, name) {
      this.clearInfErr();
      if (!this.mapDisplayData.has(name)) {
        this.loading = true;
        projectService
          .getProjectLazyInfoDisplay(this.projectId, id)
          .then((response) => {
            let newLength = this.data.displays.push(response.data);
            this.mapDisplayData.set(name, newLength - 1);
            this.changeComponent(name, 'DisplayDataRequest');
          })
          .catch((error) => {
            this.errored = errorHandler(error);
          })
          .finally(() => this.loading = false);
      }
      else {
        this.changeComponent(name, 'DisplayDataRequest');
      }
    },
    changeModuleComponent(id, name) {
      this.clearInfErr();
      if (!this.mapModule.has(name)) {
        this.loading = true;
        projectService
          .getProjectLazyInfoModule(this.projectId, id)
          .then((response) => {
            let newLength = this.data.modules.push(response.data);
            this.mapModule.set(name, newLength - 1);
            this.changeComponent(name, 'ModuleDataRequest');
          })
          .catch((error) => {
            this.errored = errorHandler(error);
          })
          .finally(() => this.loading = false);
      } else {
        this.changeComponent(name, 'ModuleDataRequest');
      }
    },
    changeComponent(name, messageType) {
      this.currentPage = name;
      this.messageType = messageType;
      if (messageType) this.timerFunction();
      this.$nextTick(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const card = this.$el && this.$el.querySelector(".card-body");
        if (card) card.scrollTop = 0;
        const main = this.$el && this.$el.closest(".main");
        if (main) main.scrollTop = 0;
      });
    },
    decideCommandAdminRights() {
      return this.data.admin > 0 && this.data.admin < 3;
    },
    decideTimeScheduleAdminRights() {
      return this.data.admin == 1 || this.data.admin == 2;
    },
    decideAetAdminRights() {
      return this.data.admin > 1;
    },
    getConnectionStatus() {
      return this.connected
        ? this.$i18n.t("project.projectViewer.connect")
        : this.$i18n.t("project.projectViewer.disconnect");
    },
    getConnectionStatusColor() {
      return this.connected ? "color:green" : "color:red";
    },
    sendValueValidator(msg) {
      this.clearInfErr();
      if (!this.connected) {
        this.errored = this.$i18n.t("project.projectViewer.errSendStopped");
        return false;
      }
      if (!msg.success) {
        this.errored = msg.value;
        return false;
      }
      return true;
    },
    sendValueFromModule(pair, callback) {
      if (!this.sendValueValidator(pair)) {
        callback(-1);
        return;
      }
      this.moduleSetCallback = callback;
      this.connection
        .invoke(
          "SendNewModuleValue",
          pair.vari,
          pair.value,
          pair.repre,
          pair.type
        )
        .catch((err) => {
          this.errored = this.$i18n.t("project.projectViewer.errSendInvoke");
          console.log(err);
          callback(-1);
          this.moduleSetCallback = null;
        });
    },
    alterVariableCases(variable) {
      let split = variable.split('.');
      split[0] = split[0].toLowerCase();
      return split.join(".");
    },
    sendValueFromDisplay(pair) {
      if (!this.sendValueValidator(pair)) return;
      this.connection
        .invoke("SendNewDisplayValue", pair.tag, pair.value)
        .catch((err) => {
          this.errored = this.$i18n.t("project.projectViewer.errSendInvoke");
          console.log(err);
        });
    },
    showInfoError(positive, text) {
      this.clearInfErr();
      if (!positive) this.errored = text;
      else this.informed = text;
    },
    toggleDisplayFullscreen() {
      const views = this.$refs.displayView;
      const view = Array.isArray(views) ? views[0] : views;
      if (view && view.fullScreenSwitcher) view.fullScreenSwitcher();
    },
    getFormatDateString(value) {
      if (!value || !value.lastMessage) return;
      return new Date(value.lastMessage + "Z").toUTCString();
    },
    showWatchdog() {
      if (this.watchdogStationId == null) return;
      const stationId = this.watchdogStationId;
      this.clearInfErr();
      projectService
        .getProjectWatchdog(this.projectId, stationId)
        .then((response) => {
          if (Number(this.watchdogStationId) !== Number(stationId)) return;
          this.watchdog = response.data;
        })
        .catch((error) => {
          this.errored = errorHandler(error);
        });
    },
  },
};
</script>

<style scoped>
.view-project {
  width: 100%;
}
.view-project--display {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.view-project--display > .navbar {
  flex-shrink: 0;
}
.project-navbar {
  min-height: 32px;
  padding: 0 0.5rem;
  font-size: 0.875rem;
  flex-wrap: wrap;
}
.project-navbar >>> .navbar-brand {
  font-size: 0.875rem;
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
  margin-right: 0.5rem;
}
.project-navbar >>> .nav-link {
  padding: 0.25rem 0.5rem;
}
.project-navbar >>> .navbar-toggler {
  padding: 0.15rem 0.4rem;
  font-size: 0.9rem;
}
.display-title-group {
  min-width: 0;
  max-width: 45%;
}
.display-title {
  min-width: 0;
}
.display-fill-card {
  position: relative;
  flex: 1;
  min-height: 0;
  margin-bottom: 0;
}
.display-fill-card >>> .card-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 4px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.4) transparent;
  -webkit-overflow-scrolling: touch;
}
.display-status-alert {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  width: auto;
  max-width: calc(100% - 32px);
}
.display-status-alert >>> .alert {
  margin-bottom: 0;
}
.display-fill-card >>> .card-body::-webkit-scrollbar {
  width: 8px;
}
.display-fill-card >>> .card-body::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.35);
  border-radius: 4px;
}
@media (pointer: coarse) {
  .display-fill-card >>> .card-body {
    padding-right: 12px;
  }
}
.display-host {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}
.display-host:has(.is-showing-graph) {
  height: auto;
  min-height: 100%;
  flex: 0 0 auto;
}
.watchdog-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.watchdog-select {
  flex: 1 1 14rem;
  width: auto;
  max-width: 100%;
  min-width: 0;
}
.watchdog-value {
  flex: 1 1 12rem;
  min-width: 0;
}
</style>
